import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import BarChartIcon from '@mui/icons-material/BarChart';
import CodeIcon from '@mui/icons-material/Code';
import axios from '../../axiosConfig';
import { acmePageSectionCardSx, acmeBillingSectionContentSx, acmePageBlockTitleSx } from '../../theme/acmeTheme';
import {
  PageShell,
  PageHeader,
  PageBlockTitle,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitleText,
  DialogBody,
  DialogFooter,
  Table,
  Input,
} from '../ui';
import PageSnackbars from '../common/PageSnackbars';
import { usePageSnackbar } from '../common/usePageSnackbar';
import { TextCancelButton } from '../button';

const DEFAULT_THEME = {
  primaryColor: '#300259',
  buttonText: 'Subscribe',
  backgroundColor: '#ffffff',
  textColor: '#2e2e2e',
  borderRadius: '8px',
};

const EMAIL_MENU = 'Email Marketing';

const dialogSectionSx = {
  mb: 3.5,
  '&:last-child': { mb: 0 },
};

const subscribeFormDialogPaperSx = {
  my: { xs: 3, sm: 5 },
  mx: { xs: 2, sm: 3 },
  maxHeight: 'calc(100vh - 80px)',
  display: 'flex',
  flexDirection: 'column',
};

const subscribeFormDialogTitleSx = {
  ...acmePageBlockTitleSx,
  textAlign: 'center',
  px: { xs: 3, sm: 4 },
  pt: { xs: 3, sm: 4 },
  pb: 1.5,
};

const subscribeFormDialogBodySx = {
  ...acmeBillingSectionContentSx,
  flex: '1 1 auto',
  overflowY: 'auto',
  px: { xs: 3, sm: 4 },
  py: { xs: 2.5, sm: 3 },
  '&.MuiDialogContent-root': {
    overflowY: 'auto',
  },
};

const subscribeFormDialogFooterSx = {
  px: { xs: 3, sm: 4 },
  pt: 2,
  pb: { xs: 3, sm: 4 },
  justifyContent: 'center',
  gap: 1.5,
};

const dialogInputSx = {
  '& .MuiInputLabel-root.MuiInputLabel-shrink': {
    backgroundColor: '#fff',
    px: 0.75,
    ml: -0.25,
  },
};

const fieldCardSx = {
  p: 2,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  bgcolor: 'background.paper',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  flexWrap: 'wrap',
  minHeight: 56,
};

const fieldCardTitleSx = {
  fontSize: '0.875rem',
  fontWeight: 600,
  lineHeight: 1.5,
  flexShrink: 0,
};

const fieldCardChecksSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 2,
  flexWrap: 'wrap',
  ml: 'auto',
};

const checkboxLabelSx = {
  m: 0,
  '& .MuiFormControlLabel-label': { fontSize: '0.875rem', lineHeight: 1.5 },
};

const instructionTextSx = {
  fontSize: '0.875rem',
  lineHeight: 1.6,
  color: 'text.secondary',
};

const emptyForm = {
  name: '',
  groupName: '',
  doubleOptIn: false,
  requireConsent: true,
  consentText: 'I agree to receive marketing emails and accept the privacy policy.',
  privacyPolicyUrl: '',
  successMessage: 'Thank you! You have been subscribed.',
  redirectUrl: '',
  allowedDomains: [],
  active: true,
  fields: {
    email: { enabled: true, required: true, label: 'Email' },
    firstName: { enabled: false, required: false, label: 'First name' },
    lastName: { enabled: false, required: false, label: 'Last name' },
  },
  theme: { ...DEFAULT_THEME },
};

function getGroupName(group) {
  return group?.groupName || group?.name || '';
}

function FormInput({ sx, ...props }) {
  return <Input sx={{ ...dialogInputSx, ...sx }} {...props} />;
}

function CopyButton({ text, label }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  return (
    <Tooltip title={copied ? 'Copied!' : label}>
      <Button size="small" variant="outlined" onClick={handleCopy} startIcon={<ContentCopyIcon />}>
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </Tooltip>
  );
}

export default function SubscribeForms() {
  const { setSuccessMessage, setErrorMessage, snackbarProps } = usePageSnackbar();
  const [forms, setForms] = useState([]);
  const [groups, setGroups] = useState([]);
  const [formsLoading, setFormsLoading] = useState(true);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [embedDialogOpen, setEmbedDialogOpen] = useState(false);
  const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [selectedForm, setSelectedForm] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [embedTab, setEmbedTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [allowedDomainsText, setAllowedDomainsText] = useState('');

  const loadForms = useCallback(async () => {
    setFormsLoading(true);
    try {
      const res = await axios.get('/api/subscribe-forms');
      setForms(res.data || []);
    } catch (err) {
      setForms([]);
      setErrorMessage(err.response?.data?.error || 'Failed to load subscribe forms');
    } finally {
      setFormsLoading(false);
    }
  }, [setErrorMessage]);

  const loadGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const res = await axios.get('/api/contacts/groups');
      setGroups(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setGroups([]);
      console.error('Failed to load contact groups:', err);
    } finally {
      setGroupsLoading(false);
    }
  }, []);

  const loadData = useCallback(async () => {
    await Promise.all([loadForms(), loadGroups()]);
  }, [loadForms, loadGroups]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreateDialog = () => {
    setEditingForm(null);
    setFormData({ ...emptyForm, theme: { ...DEFAULT_THEME } });
    setAllowedDomainsText('');
    setDialogOpen(true);
  };

  const openEditDialog = (form) => {
    setEditingForm(form);
    setFormData({
      name: form.name,
      groupName: form.groupName,
      doubleOptIn: form.doubleOptIn,
      requireConsent: form.requireConsent,
      consentText: form.consentText,
      privacyPolicyUrl: form.privacyPolicyUrl || '',
      successMessage: form.successMessage,
      redirectUrl: form.redirectUrl || '',
      allowedDomains: form.allowedDomains || [],
      active: form.active !== false,
      fields: form.fields || emptyForm.fields,
      theme: { ...DEFAULT_THEME, ...(form.theme || {}) },
    });
    setAllowedDomainsText((form.allowedDomains || []).join('\n'));
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.groupName.trim()) {
      setErrorMessage('Name and contact group are required');
      return;
    }

    setSaving(true);
    const payload = {
      ...formData,
      allowedDomains: allowedDomainsText
        .split(/[\n,]+/)
        .map((d) => d.trim())
        .filter(Boolean),
    };

    try {
      if (editingForm) {
        await axios.put(`/api/subscribe-forms/${editingForm._id}`, payload);
        setSuccessMessage('Form updated');
      } else {
        await axios.post('/api/subscribe-forms', payload);
        setSuccessMessage('Form created');
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (form) => {
    if (!window.confirm(`Delete form "${form.name}"?`)) return;
    try {
      await axios.delete(`/api/subscribe-forms/${form._id}`);
      setSuccessMessage('Form deleted');
      loadData();
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Failed to delete form');
    }
  };

  const openEmbedDialog = (form) => {
    setSelectedForm(form);
    setEmbedTab(0);
    setEmbedDialogOpen(true);
  };

  const openAnalytics = async (form) => {
    setSelectedForm(form);
    setAnalyticsDialogOpen(true);
    setAnalyticsLoading(true);
    try {
      const res = await axios.get(`/api/subscribe-forms/${form._id}/analytics`);
      setAnalytics(res.data);
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const updateField = (path, value) => {
    setFormData((prev) => {
      const next = { ...prev };
      if (path.startsWith('fields.')) {
        const [, field, key] = path.split('.');
        next.fields = {
          ...next.fields,
          [field]: { ...next.fields[field], [key]: value },
        };
      } else if (path.startsWith('theme.')) {
        const [, key] = path.split('.');
        next.theme = { ...next.theme, [key]: value };
      } else {
        next[path] = value;
      }
      return next;
    });
  };

  return (
    <PageShell>
      <PageHeader
        title="Subscribe Forms"
        description="Create embeddable subscription forms for your website. New subscribers are added directly to your contact groups."
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
            New form
          </Button>
        }
      />

      <Card sx={{ ...acmePageSectionCardSx, mb: 2 }}>
        <CardContent>
          <PageBlockTitle>How it works</PageBlockTitle>
          <Typography sx={{ ...instructionTextSx, mb: 2 }}>
            Build a subscription form here, embed it on your website, and every new subscriber is
            added automatically to the contact group you choose.
          </Typography>

          <Box component="ol" sx={{ m: 0, pl: 2.5, '& li': { mb: 1.25 } }}>
            <li>
              <Typography sx={instructionTextSx}>
                <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  Step 1.
                </Box>{' '}
                Create a contact group if you do not have one yet:{' '}
                <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  {EMAIL_MENU} → Contact Groups → Create group
                </Box>
                .
              </Typography>
            </li>
            <li>
              <Typography sx={instructionTextSx}>
                <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  Step 2.
                </Box>{' '}
                Click <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>New form</Box> on
                this page.
              </Typography>
            </li>
            <li>
              <Typography sx={instructionTextSx}>
                <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  Step 3.
                </Box>{' '}
                Name your form, select a contact group, and configure fields and settings.
              </Typography>
            </li>
            <li>
              <Typography sx={instructionTextSx}>
                <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  Step 4.
                </Box>{' '}
                Click <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>Embed</Box> and copy
                the iframe or script code.
              </Typography>
            </li>
            <li>
              <Typography sx={instructionTextSx}>
                <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  Step 5.
                </Box>{' '}
                Paste the code into your website.
              </Typography>
            </li>
            <li>
              <Typography sx={instructionTextSx}>
                <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  Step 6.
                </Box>{' '}
                Subscribers appear in{' '}
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {EMAIL_MENU} → Contact Groups
                </Box>
                . View performance with{' '}
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>Stats</Box>.
              </Typography>
            </li>
          </Box>

          {!groupsLoading && groups.length === 0 ? (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 1,
                bgcolor: 'warning.light',
                color: 'warning.contrastText',
              }}
            >
              <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.6 }}>
                You do not have any contact groups yet. Create one under{' '}
                <strong>{EMAIL_MENU} → Contact Groups</strong>, then return here to create a form.
              </Typography>
            </Box>
          ) : null}
        </CardContent>
      </Card>

      <Card sx={acmePageSectionCardSx}>
        <CardContent>
          {formsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : forms.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography color="text.secondary" mb={2}>
                No subscribe forms yet. Create one to embed on your website.
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
                Create your first form
              </Button>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Group</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Views</TableCell>
                  <TableCell>Submissions</TableCell>
                  <TableCell>Confirmed</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {forms.map((form) => (
                  <TableRow key={form._id}>
                    <TableCell>{form.name}</TableCell>
                    <TableCell>{form.groupName}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={form.active ? 'Active' : 'Inactive'}
                        color={form.active ? 'success' : 'default'}
                      />
                      {form.doubleOptIn ? (
                        <Chip size="small" label="Double opt-in" sx={{ ml: 0.5 }} />
                      ) : null}
                    </TableCell>
                    <TableCell>{form.stats?.views ?? 0}</TableCell>
                    <TableCell>{form.stats?.submissions ?? 0}</TableCell>
                    <TableCell>{form.stats?.confirmed ?? 0}</TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        <Button size="small" startIcon={<CodeIcon />} onClick={() => openEmbedDialog(form)}>
                          Embed
                        </Button>
                        <Button size="small" startIcon={<BarChartIcon />} onClick={() => openAnalytics(form)}>
                          Stats
                        </Button>
                        <Button size="small" startIcon={<EditIcon />} onClick={() => openEditDialog(form)}>
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={() => handleDelete(form)}
                        >
                          Delete
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: subscribeFormDialogPaperSx }}
      >
        <DialogTitleText sx={subscribeFormDialogTitleSx}>
          {editingForm ? 'Edit subscribe form' : 'Create subscribe form'}
        </DialogTitleText>
        <DialogBody sx={subscribeFormDialogBodySx}>
          <Box sx={dialogSectionSx}>
            <PageBlockTitle gutterBottom>General</PageBlockTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormInput
                  label="Form name"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="Website footer signup"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormInput
                  select
                  label="Contact group"
                  value={formData.groupName}
                  onChange={(e) => updateField('groupName', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (value) =>
                      value ? (
                        value
                      ) : (
                        <Box component="span" sx={{ color: 'text.secondary' }}>
                          Select a contact group
                        </Box>
                      ),
                  }}
                  disabled={groupsLoading}
                  helperText={
                    groupsLoading
                      ? 'Loading contact groups…'
                      : groups.length === 0
                        ? `Create a group under ${EMAIL_MENU} → Contact Groups first`
                        : 'Subscribers will be added to this group'
                  }
                >
                  {groups.map((group) => {
                    const name = getGroupName(group);
                    return (
                      <MenuItem key={name} value={name}>
                        {name}
                      </MenuItem>
                    );
                  })}
                </FormInput>
              </Grid>
            </Grid>
          </Box>

          <Box sx={dialogSectionSx}>
            <PageBlockTitle gutterBottom>Fields</PageBlockTitle>
            <Grid container spacing={1.5}>
              {['firstName', 'lastName'].map((field) => (
                <Grid item xs={12} sm={6} key={field}>
                  <Box sx={fieldCardSx}>
                    <Typography sx={fieldCardTitleSx}>
                      {field === 'firstName' ? 'First name' : 'Last name'}
                    </Typography>
                    <Box sx={fieldCardChecksSx}>
                      <FormControlLabel
                        sx={checkboxLabelSx}
                        control={
                          <Checkbox
                            size="small"
                            checked={formData.fields[field]?.enabled}
                            onChange={(e) => updateField(`fields.${field}.enabled`, e.target.checked)}
                          />
                        }
                        label="Show on form"
                      />
                      {formData.fields[field]?.enabled ? (
                        <FormControlLabel
                          sx={checkboxLabelSx}
                          control={
                            <Checkbox
                              size="small"
                              checked={formData.fields[field]?.required}
                              onChange={(e) => updateField(`fields.${field}.required`, e.target.checked)}
                            />
                          }
                          label="Required"
                        />
                      ) : null}
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box sx={dialogSectionSx}>
            <PageBlockTitle gutterBottom>Settings</PageBlockTitle>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  sx={checkboxLabelSx}
                  control={
                    <Checkbox
                      size="small"
                      checked={formData.doubleOptIn}
                      onChange={(e) => updateField('doubleOptIn', e.target.checked)}
                    />
                  }
                  label="Double opt-in (confirm via email)"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  sx={checkboxLabelSx}
                  control={
                    <Checkbox
                      size="small"
                      checked={formData.active}
                      onChange={(e) => updateField('active', e.target.checked)}
                    />
                  }
                  label="Form is active"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  sx={checkboxLabelSx}
                  control={
                    <Checkbox
                      size="small"
                      checked={formData.requireConsent}
                      onChange={(e) => updateField('requireConsent', e.target.checked)}
                    />
                  }
                  label="Require consent checkbox"
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormInput
                  label="Consent text"
                  value={formData.consentText}
                  onChange={(e) => updateField('consentText', e.target.value)}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormInput
                  label="Privacy policy URL"
                  value={formData.privacyPolicyUrl}
                  onChange={(e) => updateField('privacyPolicyUrl', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormInput
                  label="Redirect URL after subscribe"
                  value={formData.redirectUrl}
                  onChange={(e) => updateField('redirectUrl', e.target.value)}
                  helperText="Optional"
                />
              </Grid>
              <Grid item xs={12}>
                <FormInput
                  label="Success message"
                  value={formData.successMessage}
                  onChange={(e) => updateField('successMessage', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <FormInput
                  label="Allowed domains"
                  value={allowedDomainsText}
                  onChange={(e) => setAllowedDomainsText(e.target.value)}
                  multiline
                  rows={3}
                  helperText="One domain per line. Leave empty to allow all domains."
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={dialogSectionSx}>
            <PageBlockTitle gutterBottom>Appearance</PageBlockTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormInput
                  label="Button color"
                  type="color"
                  value={formData.theme.primaryColor}
                  onChange={(e) => updateField('theme.primaryColor', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormInput
                  label="Background color"
                  type="color"
                  value={formData.theme.backgroundColor}
                  onChange={(e) => updateField('theme.backgroundColor', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormInput
                  label="Button text"
                  value={formData.theme.buttonText}
                  onChange={(e) => updateField('theme.buttonText', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogBody>
        <DialogFooter sx={subscribeFormDialogFooterSx}>
          <TextCancelButton onClick={() => setDialogOpen(false)}>Cancel</TextCancelButton>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : editingForm ? 'Save changes' : 'Create form'}
          </Button>
        </DialogFooter>
      </Dialog>

      <Dialog open={embedDialogOpen} onClose={() => setEmbedDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitleText>Embed code — {selectedForm?.name}</DialogTitleText>
        <DialogBody sx={acmeBillingSectionContentSx}>
          <Tabs value={embedTab} onChange={(_, v) => setEmbedTab(v)} sx={{ mb: 2 }}>
            <Tab label="Iframe" />
            <Tab label="Script tag" />
          </Tabs>
          {embedTab === 0 ? (
            <Box>
              <Typography sx={instructionTextSx} mb={1}>
                Paste this iframe into your website HTML:
              </Typography>
              <Input
                multiline
                rows={4}
                value={selectedForm?.embedIframeCode || ''}
                InputProps={{ readOnly: true }}
              />
              <Box mt={1}>
                <CopyButton text={selectedForm?.embedIframeCode || ''} label="Copy iframe code" />
              </Box>
            </Box>
          ) : (
            <Box>
              <Typography sx={instructionTextSx} mb={1}>
                Paste this script tag where you want the form to appear:
              </Typography>
              <Input
                multiline
                rows={5}
                value={selectedForm?.embedScriptCode || ''}
                InputProps={{ readOnly: true }}
              />
              <Box mt={1}>
                <CopyButton text={selectedForm?.embedScriptCode || ''} label="Copy script code" />
              </Box>
            </Box>
          )}
          <Typography sx={{ ...instructionTextSx, mt: 2 }}>
            Preview:{' '}
            <a
              href={`/embed/${selectedForm?.publicId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#300259' }}
            >
              Open form preview
            </a>
          </Typography>
        </DialogBody>
        <DialogFooter>
          <Button onClick={() => setEmbedDialogOpen(false)}>Close</Button>
        </DialogFooter>
      </Dialog>

      <Dialog
        open={analyticsDialogOpen}
        onClose={() => setAnalyticsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitleText>Analytics — {selectedForm?.name}</DialogTitleText>
        <DialogBody sx={acmeBillingSectionContentSx}>
          {analyticsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : analytics ? (
            <Box>
              <Grid container spacing={2} mb={3}>
                {[
                  ['Views', analytics.summary.views],
                  ['Submissions', analytics.summary.submissions],
                  ['Confirmed', analytics.summary.confirmed],
                  ['Errors', analytics.summary.errors],
                  ['Conversion', `${analytics.summary.conversionRate}%`],
                  ['Confirm rate', `${analytics.summary.confirmRate}%`],
                ].map(([label, value]) => (
                  <Grid item xs={6} md={4} key={label}>
                    <Card sx={{ ...acmePageSectionCardSx, textAlign: 'center', p: 2 }}>
                      <Typography variant="h4">{value}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {label}
                      </Typography>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <PageBlockTitle>Recent events</PageBlockTitle>
              {analytics.recentEvents?.length ? (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Event</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Email</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytics.recentEvents.map((ev) => (
                      <TableRow key={ev._id}>
                        <TableCell>{new Date(ev.createdAt).toLocaleString()}</TableCell>
                        <TableCell>
                          <Chip size="small" label={ev.eventType} />
                        </TableCell>
                        <TableCell>{ev.sourceDomain || '—'}</TableCell>
                        <TableCell>{ev.email || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography color="text.secondary">No events yet</Typography>
              )}
            </Box>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button onClick={() => setAnalyticsDialogOpen(false)}>Close</Button>
        </DialogFooter>
      </Dialog>

      <PageSnackbars {...snackbarProps} />
    </PageShell>
  );
}
